import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import LineItemDescription from './LineItemDescription';
import LineItemTrade from './LineItemTrade';
import LineItemSell from './LineItemSell';
import AlternativeItemCard from './AlternativeItemCard';
import AddToScheduleModal from '../../../Invoices/AddToScheduleModal';
import { Trash2, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import API_BASE from '../../../../../utils/api.js';

const proxyImg = (url) =>
  url ? `${API_BASE}/api/upload/proxy-image?url=${encodeURIComponent(url)}` : '';

/* ---------------------------------------------
   Line Item Card
--------------------------------------------- */
export default function LineItemCard({
  it,
  updateItem,
  removeItem,
  rooms,
  schedules,
  onScheduleCreated,
  userId,
  projectId,
  invoiceId,
  invoiceNumber,
  onRoomCreated,
  hideDeposit = false,
  hideAlternatives = false,
  catalog = [],
  freightDestinations = [],
  readOnly = false,
  invoicedAs = '',
  onMoveUp,
  onMoveDown,
  isFirst = false,
  isLast = false,
}) {
  const [focusedRoomId, setFocusedRoomId] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const alternatives = it.alternatives || [];
  const selectedAlt = it.selectedAltId ? alternatives.find(a => a.altId === it.selectedAltId) : null;

  const [altsOpen, setAltsOpen] = useState(!!selectedAlt);

  function addAlternative() {
    const newAlt = {
      altId: uuidv4(),
      name: '',
      description: '',
      imageUrl: '',
      tradePrice: 0,
      qty: it.qty || 1,
      markup: { type: '%', value: 0 },
      clientUnit: 0,
      shipping: 0,
      shippingMarkup: { type: '%', value: 0 },
      clientShipping: 0,
      tariff: 0,
      tariffMarkup: { type: '%', value: 0 },
      clientTariff: 0,
      taxable: false,
      shippingTaxable: false,
      tariffTaxable: false,
      lineMerch: 0,
      lineTotal: 0,
      vendor: '',
      unitType: it.unitType || 'Each',
      color: '',
      finishes: '',
      materials: '',
      dimensions: {},
      customSpecs: [],
      link: '',
    };
    updateItem(it.itemId, { alternatives: [...alternatives, newAlt] });
    setAltsOpen(true);
  }

  function updateAlternative(altId, patch) {
    const updated = alternatives.map((a) => (a.altId === altId ? { ...a, ...patch } : a));
    updateItem(it.itemId, { alternatives: updated });
  }

  function removeAlternative(altId) {
    const updated = alternatives.filter((a) => a.altId !== altId);
    updateItem(it.itemId, { alternatives: updated });
  }


  /* ---------------------------------------------
     Create a new room in Library (if not exists)
  --------------------------------------------- */
  async function handleCreateRoom(roomName) {
    const trimmed = roomName.trim();
    if (!trimmed) return;

    // Optimistic assignment avoids save timing races.
    updateItem(it.itemId, { room: trimmed });

    try {
      const res = await fetch(`${API_BASE}/api/library/${userId}/create-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          projectId,
          name: trimmed,
        }),
      });

      if (!res.ok) throw new Error('Failed to create room.');

      const newRoom = await res.json();

      // Push room up to ProposalForm
      onRoomCreated?.(newRoom);

      // Keep item room in sync with canonical API name.
      if (newRoom?.name) {
        updateItem(it.itemId, { room: newRoom.name });
      }
    } catch (err) {
      console.error('Room creation error:', err);
    }
  }

  return (
    <div className="line-item-card if-card">
      {/* IMAGE */}
      <div className="li-col li-img">
        <div
          className="li-thumb"
          style={{ backgroundImage: it.imageUrl ? `url(${proxyImg(it.imageUrl)})` : 'none' }}
        />
      </div>

      {/* DESCRIPTION — includes ROOM FIELD + DROPDOWN */}
      <LineItemDescription
        it={it}
        updateItem={updateItem}
        rooms={rooms}
        userId={userId}
        projectId={projectId}
        focusedRoomId={focusedRoomId}
        setFocusedRoomId={setFocusedRoomId}
        handleCreateRoom={handleCreateRoom}
        wrapperClass="li-room-wrapper"
        readOnly={readOnly}
        schedules={schedules}
        onScheduleCreated={onScheduleCreated}
      />

      {/* TRADE COLUMN */}
      <LineItemTrade it={it} updateItem={updateItem} readOnly={readOnly} freightDestinations={freightDestinations} />

      {/* SELL COLUMN */}
      <LineItemSell it={it} updateItem={updateItem} hideDeposit={hideDeposit} readOnly={readOnly} />

      {/* ACTIONS */}
      <div className="li-col li-actions">
        {invoicedAs && (
          <span className="invoiced-item-badge" title="Billed on this invoice — no longer editable on the proposal">
            Invoiced · {invoicedAs}
          </span>
        )}

        <label className="chk-label">
          <input
            type="checkbox"
            checked={!!it.taxable}
            disabled={readOnly}
            onChange={(e) => updateItem(it.itemId, { taxable: e.target.checked })}
          />
          Taxable Item
        </label>

        <label className="chk-label">
          <input
            type="checkbox"
            checked={!!it.shippingTaxable}
            disabled={readOnly}
            onChange={(e) => updateItem(it.itemId, { shippingTaxable: e.target.checked })}
          />
          Taxable Shipping
        </label>

        <label className="chk-label">
          <input
            type="checkbox"
            checked={!!it.tariffTaxable}
            disabled={readOnly}
            onChange={(e) => updateItem(it.itemId, { tariffTaxable: e.target.checked })}
          />
          Taxable Tariff
        </label>

        <div className="client-approval-status">
          {it.clientApproved === true && (
            readOnly ? (
              <span className="client-approval-badge client-approval--accepted">
                {selectedAlt ? `✓ Accepted · chose ${selectedAlt.name || 'alternative'}` : '✓ Accepted'}
              </span>
            ) : (
              <button
                type="button"
                className="client-approval-badge client-approval--accepted client-approval--resettable"
                onClick={() => updateItem(it.itemId, { clientApproved: null })}
                title="Reset to pending"
              >
                {selectedAlt ? `✓ Accepted · chose ${selectedAlt.name || 'alternative'}` : '✓ Accepted'}
              </button>
            )
          )}
          {it.clientApproved === false && (
            readOnly ? (
              <span className="client-approval-badge client-approval--declined">✕ Declined</span>
            ) : (
              <button
                type="button"
                className="client-approval-badge client-approval--declined client-approval--resettable"
                onClick={() => updateItem(it.itemId, { clientApproved: null })}
                title="Reset to pending"
              >
                ✕ Declined
              </button>
            )
          )}
          {(it.clientApproved === null || it.clientApproved === undefined) && (
            <span className="client-approval-badge client-approval--pending">Pending</span>
          )}
        </div>

        {Boolean(invoiceId) && (
          <div className="add-to-schedule-area">
            {(it.scheduleItemIds || []).length > 0 && (
              <span className="added-to-schedule-badge">
                ✓ Added to Schedule ({it.scheduleItemIds.length} room{it.scheduleItemIds.length !== 1 ? 's' : ''})
              </span>
            )}
            <button
              type="button"
              className="add-to-schedule-btn"
              onClick={() => setShowScheduleModal(true)}
            >
              {(it.scheduleItemIds || []).length > 0 ? '+ Add another room' : '+ Add to Schedule'}
            </button>
          </div>
        )}

        {!readOnly && (onMoveUp || onMoveDown) && (
          <div className="li-reorder-actions">
            <button
              type="button"
              className="li-reorder-btn"
              onClick={onMoveUp}
              disabled={isFirst}
              title="Move up"
            >
              <ChevronUp size={14} />
            </button>
            <button
              type="button"
              className="li-reorder-btn"
              onClick={onMoveDown}
              disabled={isLast}
              title="Move down"
            >
              <ChevronDown size={14} />
            </button>
          </div>
        )}

        {!readOnly && (
          <button className="remove-line" onClick={() => removeItem(it.itemId)} title="Remove item">
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* ALTERNATIVES SECTION */}
      {!hideAlternatives && <div className="li-alternatives-section">
        <div className="li-alts-header">
          {alternatives.length > 0 ? (
            <button
              className="li-alts-toggle"
              onClick={() => setAltsOpen((o) => !o)}
            >
              {altsOpen ? '▲' : '▼'} {alternatives.length} alternative{alternatives.length !== 1 ? 's' : ''}
            </button>
          ) : (
            <span className="li-alts-empty">No alternatives</span>
          )}
          {!readOnly && alternatives.length < 3 && (
            <button className="li-add-alt-btn" onClick={addAlternative} title="Add alternative">
              <Plus size={13} /> Add alternative
            </button>
          )}
        </div>

        {altsOpen && alternatives.length > 0 && (
          <div className="li-alts-list">
            {alternatives.map((alt, idx) => (
              <AlternativeItemCard
                key={alt.altId}
                alt={alt}
                index={idx}
                catalog={catalog}
                onUpdate={(patch) => updateAlternative(alt.altId, patch)}
                onRemove={() => removeAlternative(alt.altId)}
                readOnly={readOnly}
                isClientSelected={alt.altId === it.selectedAltId}
              />
            ))}
          </div>
        )}
      </div>}

      {showScheduleModal && (
        <AddToScheduleModal
          it={it}
          rooms={rooms}
          userId={userId}
          projectId={projectId}
          invoiceId={invoiceId}
          invoiceNumber={invoiceNumber}
          onClose={() => setShowScheduleModal(false)}
          onAdded={(scheduleItemId) => {
            updateItem(it.itemId, {
              scheduleItemIds: [...(it.scheduleItemIds || []), scheduleItemId],
            });
            setShowScheduleModal(false);
          }}
        />
      )}
    </div>
  );
}

LineItemCard.propTypes = {
  it: PropTypes.object.isRequired,
  updateItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  rooms: PropTypes.array.isRequired,
  schedules: PropTypes.array,
  onScheduleCreated: PropTypes.func,
  userId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  invoiceId: PropTypes.string,
  invoiceNumber: PropTypes.string,
  onRoomCreated: PropTypes.func.isRequired,
  hideDeposit: PropTypes.bool,
  catalog: PropTypes.array,
  freightDestinations: PropTypes.array,
  invoicedAs: PropTypes.string,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
};
